<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import draggable from 'vuedraggable';
import TasksAPI from 'dashboard/api/tasks';
import TaskModal from 'dashboard/modules/tasks/components/TaskModal.vue';
import TaskInlineCreate from 'dashboard/modules/tasks/components/TaskInlineCreate.vue';
import TaskListItem from 'dashboard/modules/tasks/components/TaskListItem.vue';
import TaskProgressBar from 'dashboard/modules/tasks/components/TaskProgressBar.vue';
import TaskFilters from 'dashboard/modules/tasks/components/TaskFilters.vue';
import TaskSort from 'dashboard/modules/tasks/components/TaskSort.vue';

const props = defineProps({
  card: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { updateTask } = useTaskActions();

function storageKey(card) {
  const pipelineId = card.pipeline_id ?? card.pipeline?.id ?? 'default';
  return `kanban_task_filters_${pipelineId}`;
}

function loadSavedState(card) {
  try {
    return JSON.parse(localStorage.getItem(storageKey(card)) ?? 'null') ?? {};
  } catch {
    return {};
  }
}

function saveState(filters, by, dir) {
  try {
    localStorage.setItem(
      storageKey(props.card),
      JSON.stringify({ filters, by, dir })
    );
  } catch {
    // quota exceeded — ignore
  }
}

const saved = loadSavedState(props.card);

const tasks = ref([]);
const localTasks = ref([]);
const dragging = ref(false);
const isLoading = ref(false);
const selectedTaskId = ref(null);
const showNewTaskForm = ref(false);
const showGroupPicker = ref(false);
const pendingApplyId = ref(null);
const groups = computed(
  () => store.getters['kanbanTaskGroups/getGroups'] ?? []
);
const showFilters = ref(
  Object.keys(saved.filters ?? {}).some(k => {
    const v = saved.filters[k];
    return Array.isArray(v) ? v.length > 0 : v !== '' && v != null;
  }) ||
    (saved.by && saved.by !== 'custom')
);
const activeFilters = ref(saved.filters ?? {});
const sortBy = ref(saved.by ?? 'custom');
const sortDir = ref(saved.dir ?? 'asc');

function taskPrefill(card) {
  return {
    ...(card.contact?.id
      ? {
          contact_id: card.contact.id,
          contact: card.contact,
        }
      : {}),
    kanban_card_id: card.id,
  };
}

async function fetchTasks() {
  if (!props.card?.id) return;
  isLoading.value = true;
  try {
    const requests = [TasksAPI.list({ kanban_card_id: props.card.id })];
    const contactId = props.card.contact?.id;
    if (contactId) requests.push(TasksAPI.list({ contact_id: contactId }));

    const results = await Promise.all(requests);
    const seen = new Set();
    const merged = [];
    results.forEach(({ data }) => {
      (data.data ?? []).forEach(task => {
        if (!seen.has(task.id)) {
          seen.add(task.id);
          merged.push(task);
        }
      });
    });
    tasks.value = merged;
    // eslint-disable-next-line no-use-before-define
    if (!dragging.value) applyFilters();
  } catch {
    tasks.value = [];
    localTasks.value = [];
  } finally {
    isLoading.value = false;
  }
}

const PRIORITY_ORDER = { urgent: 0, high: 1, normal: 2, no_priority: 3 };

function applyFilters() {
  const f = activeFilters.value;
  let result = tasks.value.filter(task => {
    if (f.status && task.status !== f.status) return false;
    if (f.assignee_id?.length) {
      const hasUnassigned = f.assignee_id.includes('unassigned');
      const agentIds = f.assignee_id.filter(id => id !== 'unassigned');
      const assignee = task.assignee ?? task.meta?.assignee;
      const matchUnassigned = hasUnassigned && !assignee;
      const matchAgent = agentIds.includes(assignee?.id);
      if (!matchUnassigned && !matchAgent) return false;
    }
    if (f.priority?.length && !f.priority.includes(task.priority)) return false;
    if (f.task_type?.length && !f.task_type.includes(task.task_type))
      return false;
    return true;
  });

  if (sortBy.value !== 'custom') {
    const dir = sortDir.value === 'asc' ? 1 : -1;
    result = [...result].sort((a, b) => {
      if (sortBy.value === 'priority') {
        return (
          ((PRIORITY_ORDER[a.priority] ?? 99) -
            (PRIORITY_ORDER[b.priority] ?? 99)) *
          dir
        );
      }
      if (sortBy.value === 'due_date') {
        const da = a.due_date ? new Date(a.due_date) : Infinity;
        const db = b.due_date ? new Date(b.due_date) : Infinity;
        return (da - db) * dir;
      }
      if (sortBy.value === 'created_at') {
        return (new Date(a.created_at) - new Date(b.created_at)) * dir;
      }
      if (sortBy.value === 'title') {
        return (a.title ?? '').localeCompare(b.title ?? '') * dir;
      }
      return 0;
    });
  }

  localTasks.value = result;
}

function onFilterChange(filters) {
  activeFilters.value = filters;
  saveState(filters, sortBy.value, sortDir.value);
  applyFilters();
}

function onSortByChange(val) {
  sortBy.value = val;
  sortDir.value = 'asc';
  saveState(activeFilters.value, val, 'asc');
  applyFilters();
}

function onSortDirChange(val) {
  sortDir.value = val;
  saveState(activeFilters.value, sortBy.value, val);
  applyFilters();
}

const hasActiveFilters = computed(
  () =>
    sortBy.value !== 'custom' ||
    Object.entries(activeFilters.value).some(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v !== '' && v != null
    )
);

async function handleComplete(task) {
  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
  await updateTask({
    taskId: task.id,
    status: newStatus,
    completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
  });
  await fetchTasks();
}

function handleOpen(task) {
  selectedTaskId.value = task.id;
}

function onTaskCreated() {
  showNewTaskForm.value = false;
  fetchTasks();
}

function requestApplyGroup(groupId) {
  pendingApplyId.value = groupId;
}

function cancelApplyGroup() {
  pendingApplyId.value = null;
}

async function applyGroup(groupId) {
  pendingApplyId.value = null;
  await store.dispatch('kanbanTaskGroups/apply', {
    groupId,
    cardId: props.card.id,
  });
  showGroupPicker.value = false;
  fetchTasks();
}

async function onDragEnd() {
  dragging.value = false;
  await Promise.all(
    localTasks.value.map((task, index) =>
      TasksAPI.update(task.id, { position: index })
    )
  );
}

const tasksForProgress = computed(() => {
  const f = activeFilters.value;
  return tasks.value.filter(task => {
    if (f.assignee_id?.length) {
      const hasUnassigned = f.assignee_id.includes('unassigned');
      const agentIds = f.assignee_id.filter(id => id !== 'unassigned');
      const assignee = task.assignee ?? task.meta?.assignee;
      const matchUnassigned = hasUnassigned && !assignee;
      const matchAgent = agentIds.includes(assignee?.id);
      if (!matchUnassigned && !matchAgent) return false;
    }
    if (f.priority?.length && !f.priority.includes(task.priority)) return false;
    if (f.task_type?.length && !f.task_type.includes(task.task_type))
      return false;
    return true;
  });
});

const completedCount = computed(
  () =>
    tasksForProgress.value.filter(task => task.status === 'completed').length
);
const totalCount = computed(() => tasksForProgress.value.length);

onMounted(() => {
  fetchTasks();
  store.dispatch('kanbanTaskGroups/fetch');
});
watch(() => props.card?.id, fetchTasks);
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="flex items-center justify-between gap-3">
      <h4
        class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex-shrink-0"
      >
        {{ t('KANBAN.OPPORTUNITY_MODAL.NAV_TASKS') }}
      </h4>
      <TaskProgressBar
        v-if="totalCount > 0"
        :completed="completedCount"
        :total="totalCount"
      />
      <div class="flex items-center gap-1 flex-shrink-0">
        <button
          class="size-8 flex items-center justify-center rounded-lg bg-n-slate-3 hover:bg-n-slate-4 transition-colors"
          :class="hasActiveFilters ? 'text-n-brand' : 'text-n-slate-9'"
          :title="t('TASKS.FILTERS.TITLE', 'Filtrar tarefas')"
          @click="showFilters = !showFilters"
        >
          <span class="i-lucide-filter size-5" />
        </button>

        <!-- Botão aplicar grupo -->
        <div class="relative">
          <button
            class="size-8 flex items-center justify-center rounded-lg bg-n-slate-3 hover:bg-n-slate-4 transition-colors text-n-slate-9"
            :title="$t('KANBAN.TASK_GROUPS.APPLY')"
            @click="
              showGroupPicker = !showGroupPicker;
              pendingApplyId = null;
            "
          >
            <span class="i-lucide-list-checks size-5" />
          </button>

          <template v-if="showGroupPicker">
            <div
              class="fixed inset-0 z-20"
              @click="
                showGroupPicker = false;
                pendingApplyId = null;
              "
            />
            <div
              class="absolute right-0 top-full mt-1 w-56 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 z-30"
              @click.stop
            >
              <p
                v-if="!groups.length"
                class="px-3 py-2 text-sm text-n-slate-10 italic"
              >
                {{ $t('KANBAN.TASK_GROUPS.APPLY_PICKER_EMPTY') }}
              </p>
              <template v-for="group in groups" :key="group.id">
                <button
                  v-if="pendingApplyId !== group.id"
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                  @click="requestApplyGroup(group.id)"
                >
                  <span
                    class="i-lucide-list-checks size-4 text-n-slate-10 flex-shrink-0"
                  />
                  <span class="truncate">{{ group.name }}</span>
                </button>
                <div
                  v-else
                  class="flex items-center justify-end gap-2 px-3 py-2"
                >
                  <button
                    class="text-xs text-n-slate-11 hover:text-n-slate-12 px-2 py-1 rounded"
                    @click="cancelApplyGroup"
                  >
                    {{ $t('KANBAN.TASK_GROUPS.APPLY_CANCEL') }}
                  </button>
                  <button
                    class="text-xs font-medium text-white bg-n-teal-9 hover:bg-n-teal-10 px-2 py-1 rounded transition-colors"
                    @click="applyGroup(group.id)"
                  >
                    {{ $t('KANBAN.TASK_GROUPS.APPLY_CONFIRM') }}
                  </button>
                </div>
              </template>
            </div>
          </template>
        </div>

        <button
          class="size-8 flex items-center justify-center rounded-lg bg-n-brand hover:brightness-110 text-white"
          :title="t('TASKS.CONTACT_SECTION.ADD')"
          @click="showNewTaskForm = !showNewTaskForm"
        >
          <span class="i-lucide-plus size-5" />
        </button>
      </div>
    </div>

    <!-- Painel de filtros -->
    <div
      v-if="showFilters"
      class="pt-1 pb-2 flex items-center justify-between gap-2"
    >
      <div class="flex items-center gap-2 flex-wrap">
        <TaskFilters
          :initial-filters="activeFilters"
          compact
          @filter-change="onFilterChange"
        />
      </div>
      <TaskSort
        :model-value="sortBy"
        :direction="sortDir"
        compact
        @update:model-value="onSortByChange"
        @update:direction="onSortDirChange"
      />
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-xs text-n-slate-10 py-4 text-center">
      {{ t('TASKS.LOADING') }}
    </div>

    <TaskInlineCreate
      v-if="showNewTaskForm"
      :prefill="taskPrefill(card)"
      hide-contact
      @created="onTaskCreated"
      @cancel="showNewTaskForm = false"
    />

    <!-- Empty state -->
    <div
      v-if="!isLoading && !showNewTaskForm && localTasks.length === 0"
      class="flex flex-col items-center justify-center py-8 gap-2 text-n-slate-9"
    >
      <span class="i-lucide-check-square size-6" />
      <span class="text-xs">{{ t('TASKS.CONTACT_SECTION.EMPTY') }}</span>
    </div>

    <!-- Lista de tarefas -->
    <draggable
      v-if="!isLoading && localTasks.length > 0"
      v-model="localTasks"
      item-key="id"
      handle=".task-drag-item"
      ghost-class="opacity-40"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <template #item="{ element: task }">
        <TaskListItem
          :task="task"
          hide-tags
          compact-priority
          hide-completed-info
          @open="handleOpen"
          @complete="handleComplete"
          @updated="fetchTasks"
        />
      </template>
    </draggable>

    <!-- Modal de edição da tarefa -->
    <TaskModal
      v-if="selectedTaskId"
      :task-id="selectedTaskId"
      @close="selectedTaskId = null"
      @open-card="selectedTaskId = null"
      @updated="fetchTasks"
      @deleted="
        selectedTaskId = null;
        fetchTasks();
      "
    />
  </div>
</template>
